"use client"

import Link from "next/link"
import { AlertTriangle } from "lucide-react"

import { cn } from "@/lib/utils"
import {
  MODERATION_FOOTER_DEFAULT,
  MODERATION_LEAD_DEFAULT,
  splitModerationMessage,
  type ViolationType,
} from "@/lib/moderation-client"
import {
  CONTACT_BLOCK_MESSAGE,
  GENERAL_VIOLATION_MESSAGE,
  ILLEGAL_CONTENT_MESSAGE,
  GENERAL_VIOLATION_REASON_SHORT,
  VIOLATION_REASONS_SHORT,
} from "@/lib/contact-policy"

interface ModerationNoticeProps {
  message?: string | null
  type?: ViolationType
  className?: string
}

export function ModerationNotice({ message, type, className }: ModerationNoticeProps) {
  if (!message) {
    return null
  }

  const parsed = splitModerationMessage(message)
  const normalized = message.trim()
  const lowered = normalized.toLowerCase()
  let derivedType = type
  if (!derivedType) {
    if (
      normalized === CONTACT_BLOCK_MESSAGE ||
      lowered.includes("no trader may stray beyond kraahl") ||
      lowered.includes("contact clues")
    ) {
      derivedType = "contact"
    } else if (
      normalized === ILLEGAL_CONTENT_MESSAGE ||
      lowered.includes("no illegal goods") ||
      lowered.includes("regulated content")
    ) {
      derivedType = "illegal"
    } else if (
      normalized === GENERAL_VIOLATION_MESSAGE ||
      lowered.includes("no malice, harassment")
    ) {
      derivedType = "general"
    }
  }

  const parsedLead = parsed?.lead?.trim()
  const parsedReason = parsed?.reason?.trim()
  const parsedFooter = parsed?.footer?.trim()
  const reason =
    (derivedType && VIOLATION_REASONS_SHORT[derivedType]) ||
    (parsedReason && parsedReason !== MODERATION_FOOTER_DEFAULT ? parsedReason : undefined) ||
    GENERAL_VIOLATION_REASON_SHORT
  const lead = parsedLead || MODERATION_LEAD_DEFAULT
  const footer =
    parsedFooter ||
    (parsedReason === MODERATION_FOOTER_DEFAULT && !parsedFooter ? parsedReason : undefined) ||
    MODERATION_FOOTER_DEFAULT

  return (
    <div
      className={cn(
        "mb-4 p-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-xl",
        className,
      )}
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
        <div className="flex-1 text-sm text-amber-800 dark:text-amber-200 space-y-2">
          <div className="font-semibold">Platform Security Notice</div>
          <p className="text-amber-700 dark:text-amber-300">{lead}</p>
          <p className="text-amber-800 dark:text-amber-200 font-semibold">{reason}</p>
          <p className="text-amber-700 dark:text-amber-300">
            {footer}
          </p>
          <Link href="/terms" className="underline font-medium text-amber-800 dark:text-amber-300">
            Open the Terms of Service
          </Link>
        </div>
      </div>
    </div>
  )
}


